import type { ComboboxData } from "@mantine/core";
import { IconCalendarWeek } from "@tabler/icons-react";
import {
  LegalStructureType,
  RepresentationType,
  SupplierStatus,
  YesNoChoice
} from "../../../types/api.types";
import urls from "../../../urls";
import { type Person } from "../../../utils/backend-types";
import type { FormInputData } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { PersonWizardTabValue } from "../PersonWizard.constants";

const YES_NO_OPTIONS: ComboboxData = [
  { value: YesNoChoice.yes, label: "Ja" },
  { value: YesNoChoice.no, label: "Nein" }
];

const LEGAL_STRUCTURE_OPTIONS: ComboboxData = [
  { value: LegalStructureType.adoer, label: "AdöR" },
  { value: LegalStructureType.ag, label: "AG" },
  { value: LegalStructureType.ausl_Rechtsform, label: "ausl. Rechtsform" },
  { value: LegalStructureType.bga, label: "BgA" },
  { value: LegalStructureType.ek, label: "Ek" },
  { value: LegalStructureType.ev, label: "e.V." },
  { value: LegalStructureType.eg, label: "eG" },
  { value: LegalStructureType.einzelunternehmen, label: "Einzelunternehmen" },
  { value: LegalStructureType.ewiv, label: "EWIV" },
  { value: LegalStructureType.freiberufler, label: "Freiberufler" },
  { value: LegalStructureType.gbr, label: "GbR" },
  { value: LegalStructureType.gmbh, label: "GmbH" },
  { value: LegalStructureType.kdoer, label: "KdöR" },
  { value: LegalStructureType.kg, label: "KG" },
  { value: LegalStructureType.kgaa, label: "KGaA" },
  { value: LegalStructureType.ohg, label: "OHG" },
  { value: LegalStructureType.partg, label: "PartG" },
  { value: LegalStructureType.privat, label: "Privat" },
  { value: LegalStructureType.sce, label: "SCE" },
  { value: LegalStructureType.se, label: "SE" },
  { value: LegalStructureType.sonstige, label: "Sonstige" },
  { value: LegalStructureType.stiftung, label: "Stiftung" },
  { value: LegalStructureType.vvag, label: "VVaG" }
];

const TAX_FORMS_APPLICANT_COUNTRY_OPTIONS: ComboboxData = [
  "DE",
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW"
].map((code) => ({ value: code, label: code }));

export const SUPPLIER_STATUS_OPTIONS: ComboboxData = [
  {
    value: SupplierStatus.Full,
    label: "Versorger nach § 4 Abs. 1 StromStG i. V. m. § 3 Abs. 1 StromStV"
  },
  {
    value: SupplierStatus.EigenerzeugerMitErlaubnis,
    label:
      "Eigenerzeuger mit Erlaubnis nach § 4 Abs. 1 StromStG i.V.m. § 3 Abs. 1 StromStV"
  },
  {
    value: SupplierStatus.EigenerzeugerOhneErlaubnispflicht,
    label: "Eigenerzeuger ohne Erlaubnispflicht nach § 4 Abs. 1 Satz 2 StromStG"
  },
  {
    value: SupplierStatus.Limited,
    label:
      "Eingeschränkter Versorger nach § 4 Abs. 1 StromStG i. V. m. § 2 Abs. 3 StromStV („kleiner Versorger“)"
  },
  { value: SupplierStatus.NoStatus, label: "Aktuell kein Versorgerstatus" }
];

const REPRESENTATION_TYPE_OPTIONS: ComboboxData = [
  {
    value: RepresentationType.authorized_representative_section_80_ao,
    label: "Bevollmächtigter nach § 80 AO"
  },
  {
    value:
      RepresentationType.authorized_representative_section_80_ao_relative_accountant_section_3_4_StBerG,
    label:
      "Bevollmächtigter nach § 80 AO (Angehörige steuerberatender Berufe i.S.d. §§ 3 und 4 Nr. 11 StBerG)"
  },
  {
    value: RepresentationType.universal_successor,
    label: "Gesamtrechtsnachfolger"
  },
  {
    value: RepresentationType.manager_groups_without_legal_capacity,
    label: "Geschäftsführer von nichtrechtsfähigen Personenvereinigungen"
  },
  {
    value: RepresentationType.manager_estates_section_86_26_BGB,
    label: "Geschäftsführer von Vermögensmassen nach §§ 86, 26 BGB"
  },
  {
    value: RepresentationType.legal_representative_legal_person,
    label: "Gesetzlicher Vertreter einer juristischen Person"
  },
  {
    value: RepresentationType.legal_representative_natural_person,
    label: "Gesetzlicher Vertreter natürlicher Personen"
  },
  {
    value: RepresentationType.other_representative,
    label: "Sonstiger Vertreter"
  },
  {
    value: RepresentationType.tax_representative_section_214_ao,
    label: "Steuerlicher Beauftragter nach § 214 AO"
  },
  {
    value: RepresentationType.tax_operations_manager_section_62_paragraph_1_StG,
    label: "Steuerlicher Betriebsleiter nach § 62 Abs. 1 EnergieStG"
  },
  {
    value: RepresentationType.acquiring_legal_entity,
    label: "Übernehmender Rechtsträger"
  },
  {
    value: RepresentationType.authorized_for_disposal_section_35_ao,
    label: "Verfügungsberechtigter nach § 35 AO"
  },
  {
    value: RepresentationType.asset_manager_section_34_paragraph_1_ao,
    label: "Vermögensverwalter nach § 34 Abs. 1 AO"
  },
  {
    value: RepresentationType.representative_section_81_ao,
    label: "Vertreter nach § 81 AO"
  }
];

export const PERSON_FORM_FIELD_DATA = {
  // Always visible fields (Create mode + Edit mode)
  name: {
    name: "name",
    group: PersonWizardTabValue.General,
    label: "Bezeichnung",
    type: "text",
    required: true
  },
  address: {
    name: "address",
    group: PersonWizardTabValue.General,
    label: "Anschrift",
    type: "text"
  },
  zip: {
    name: "zip",
    group: PersonWizardTabValue.General,
    label: "Postleitzahl",
    type: "text",
    maxLength: 10
  },
  city: {
    name: "city",
    group: PersonWizardTabValue.General,
    label: "Ort",
    type: "text"
  },

  // Conditional fields (in form appearance order)
  isSolarContractor: {
    name: "isSolarContractor",
    group: PersonWizardTabValue.General,
    label: "Unternehmen ist Mieterstromanbieter",
    type: "boolean"
  },
  shouldRegulatoryDutiesBeChecked: {
    name: "shouldRegulatoryDutiesBeChecked",
    group: PersonWizardTabValue.General,
    label:
      "Soll das Add-on \u201eStromsteuer\u201c für dieses Unternehmen aktiviert werden?",
    type: "boolean"
  },
  isParagraph_6EegActive: {
    name: "isParagraph_6EegActive",
    group: PersonWizardTabValue.General,
    label:
      "Soll das Add-on \u201eFinanzielle Beteiligung nach § 6 EEG\u201c für dieses Unternehmen aktiviert werden?",
    type: "boolean"
  },
  isErloesmonitoringActive: {
    name: "isErloesmonitoringActive",
    group: PersonWizardTabValue.General,
    label:
      "Soll das Add-on \u201eErlösmonitoring\u201c für dieses Unternehmen aktiviert werden?",
    type: "boolean"
  },
  isRedispatchImportActive: {
    name: "isRedispatchImportActive",
    group: PersonWizardTabValue.General,
    label:
      "Automatischer Datenimport für Redispatch- und DV-Abregelungsenergiemengen für dieses Unternehmen aktiviert?",
    type: "boolean"
  },
  addressExtra: {
    name: "addressExtra",
    group: PersonWizardTabValue.General,
    label: "Anschrift Zusatz",
    type: "text"
  },
  contact: {
    name: "contact",
    group: PersonWizardTabValue.General,
    label: "Ansprechpartner",
    type: "text"
  },
  taxFormsApplicantLegalStructure: {
    name: "taxFormsApplicantLegalStructure",
    group: PersonWizardTabValue.General,
    label: "Rechtsform",
    type: "select",
    data: LEGAL_STRUCTURE_OPTIONS,
    helpText:
      "Es können keine Mischformen angegeben werden. Handelt es sich bei ihrem Unternehmen z. B. um eine GmbH & Co. KG dann wählen Sie im Formular bitte die Option \u201eKG\u201c aus."
  },
  mastrNummerMarktakteur: {
    name: "mastrNummerMarktakteur",
    group: PersonWizardTabValue.General,
    label: "MaStR-Nummer Marktakteur",
    maxLength: 17,
    type: "text",
    helpText:
      "Bei der Registrierung als Anlagenbetreiber im Marktstammdatenregister, erhalten Sie eine MaStR-Nummer als Anlagenbetreiber. Diese besteht aus 15 Zeichen und beginnt immer mit den Buchstaben ABR. Sie können Ihre Betreibergesellschaft (falls es sich nicht um eine natürliche Person handelt) sowie die dazugehörige MaStR-Nummer auch im Marktstammdatenregister finden. Suchen Sie hierzu entweder nach dem Namen der Betreibergesellschaft oder nach der Erzeugungsanlage. In den Stammdaten der Erzeugungsanlage finden Sie die Betreibergesellschaft im Reiter \u201eAllgemeine Daten\u201c. Durch Klicken auf den Link der Betreibergesellschaften finden Sie die MaStR-Nummer der Betreibergesellschaft."
  },

  // General Partner fields
  generalPartnerName: {
    name: "generalPartnerName",
    group: PersonWizardTabValue.General,
    label: "Name der haftenden Gesellschaft inkl. Rechtsformzusatz",
    type: "text",
    placeholder: "z. B. node.energy GmbH"
  },
  generalPartnerCity: {
    name: "generalPartnerCity",
    group: PersonWizardTabValue.General,
    label: "Sitz der Gesellschaft",
    type: "text",
    placeholder: "z. B. Frankfurt am Main"
  },
  generalPartnerCourt: {
    name: "generalPartnerCourt",
    group: PersonWizardTabValue.General,
    label: "Registergericht des Sitzes",
    type: "text",
    placeholder: "z. B. Amtsgericht Frankfurt am Main"
  },
  generalPartnerRegisterNumber: {
    name: "generalPartnerRegisterNumber",
    group: PersonWizardTabValue.General,
    label: "Handelsregisternummer",
    type: "text",
    placeholder: "z. B. HRB 108778"
  },
  generalPartnerCeo: {
    name: "generalPartnerCeo",
    group: PersonWizardTabValue.General,
    label: "Name aller Geschäftsführer",
    type: "text",
    placeholder: "z. B. Manu Musterperson"
  },

  // Additional regulatory fields
  manufacturingIndustry: {
    name: "manufacturingIndustry",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Handelt es sich um ein Unternehmen des produzierenden Gewerbes im Sinne des Strom- und Energiesteuerrechts?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Die Einordnung als Unternehmen des produzierenden Gewerbes geschieht nach § 2 Nr. 3 StromStG anhand der Klassifikation der Wirtschaftszweige des statistischen Bundesamts in der Fassung von 2003 (nicht 2008). Berücksichtigung findet hierbei das verarbeitende Gewerbe (Kategorie D) und die Energie- und Wasserversorgung (E) sowie Bergbau (C) und Baugewerbe (F). Insbesondere für Betriebe ohne klare Zuordnung bzw. zwischen den einzelnen Kategorien entscheidet prinzipiell das HZA, u.a. nach Anteil der Wertschöpfung, welcher Kategorie ein Unternehmen zuzuordnen ist (§ 15 Abs. 4 StromStV)."
  },
  forestryOrAgriculture: {
    name: "forestryOrAgriculture",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Handelt es sich um ein Unternehmen der Land- oder Forstwirtschaft im Sinne des Strom- und Energiesteuerrechts?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Die Einordnung als Unternehmen der Land- oder Forstwirtschaft geschieht nach § 2 Nr. 5 StromStG anhand der Klassifikation der Wirtschaftszweige des statistischen Bundesamts in der Fassung von 2003. Berücksichtigung findet hierbei insbesondere Betriebe der Land- und Forstwirtschaft (genau Definition in Abschnitt A: u.a. Pflanzenbau, Tierhaltung, Holzgewinnung) sowie der Fischzucht (Klasse 05.02). Insbesondere für Betriebe ohne klare Zuordnung bzw. zwischen den einzelnen Kategorien entscheidet prinzipiell das HZA, u.a. nach Anteil der Wertschöpfung, welcher Kategorie ein Unternehmen zuzuordnen ist (§ 15 Abs. 4 StromStV)."
  },
  electricityOrEnergyTaxRelief_10StromstgOr_55Energiestg: {
    name: "electricityOrEnergyTaxRelief_10StromstgOr_55Energiestg",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Wird eine Strom- und/oder Energiesteuerentlastung nach § 10 StromStG bzw. § 55 EnergieStG (Spitzenausgleich) in Anspruch genommen oder ist dies zukünftig geplant?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "In voller Höhe bezahlte Strom- bzw. Energiesteuer kann zu einem Großteil " +
      "(bis zu 90% der bezahlten Steuer in Abhängigkeit der bezahlten " +
      "Rentenversicherungsbeiträge) erstattet werden. Voraussetzung ist ausdrücklich, " +
      "dass das Unternehmen dem produzierenden Gewerbe zugehörig ist (Abs. 1), genaue " +
      "Definition nach § 2 Nr. 3 StromStG anhand der Klassifikation der " +
      "Wirtschaftszweige des statistischen Bundesamts in der Fassung von 2003. Dieser " +
      "sogenannte Spitzenausgleich kann ab einer Steuerlast von 1000 Euro/a für Strom " +
      "gemäß §10 Abs. 1 StromStG gewährt werden. Abgezogen von der Erstattung wird die " +
      "tatsächliche oder fiktive Entlastung (eine tatsächliche Beantragung ist nicht " +
      "Voraussetzung) nach § 54 EnergieStG bzw. § 9b StromStG. Dies entspricht einem " +
      "ungefähren Stromverbrauch von 50 MWh. Spitzenausgleich für Erdgas zur Strom- " +
      "oder Wärmeproduktion ist gemäß § 55 Abs.1 und 3 EnergieStG ab ca. 330 MWh/a " +
      "möglich. Für Windparks und Freiflächen-PV im Regelfall mit \\u201enein\\u201c zu beantworten."
  },
  dinEnIso_50001Installed: {
    name: "dinEnIso_50001Installed",
    group: PersonWizardTabValue.RegulatoryDuties,
    label: "Energiemanagementsystem nach DIN EN ISO 50001 installiert",
    type: "select",
    data: YES_NO_OPTIONS
  },
  emasInstalled: {
    name: "emasInstalled",
    group: PersonWizardTabValue.RegulatoryDuties,
    label: "Umweltmanagementsystem nach EMAS installiert",
    type: "select",
    data: YES_NO_OPTIONS
  },
  dinEn16247_1Installed: {
    name: "dinEn16247_1Installed",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "System zur Verbesserung der Energieeffizienz nach DIN EN 16247-1 betrieben",
    type: "select",
    data: YES_NO_OPTIONS
  },
  energyTaxRelief_54Energiestg: {
    name: "energyTaxRelief_54Energiestg",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Wird eine Energiesteuerentlastung gemäß § 54 EnergieStG (prod. Gewerbe) in Anspruch genommen oder ist dies zukünftig geplant?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Für Unternehmen des produzierenden Gewerbes (genaue Definition nach § 2 Nr. 3 " +
      "StromStG anhand der Klassifikation der Wirtschaftszweige des statistischen " +
      "Bundesamts in der Fassung von 2003) kann für fossile Energieträger (Heizöl, " +
      "Erdgas, Flüssiggas), welche zur Erzeugung von Wärme oder Strom (genaue " +
      "Definition begünstigter Anlagen in §3 EnerigeStG) eingesetzt werden, vom HZA " +
      "auf Antrag (Formular 1118) eine Energiesteuerentlastung genehmigt werden. " +
      "Grundsätzlich ist dies bspw. ab einem Erdgasverbrauch von 181 MWh/a möglich. " +
      "Die Entlastung für bspw. Erdgas beträgt 1,38 €/MWh. " +
      "Für Windparks und Freiflächen-PV im Regelfall mit \u201enein\u201c zu beantworten."
  },
  energyTaxRelief_51Energiestg: {
    name: "energyTaxRelief_51Energiestg",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Wird eine Energiesteuerentlastung für bestimmte Prozesse und Verfahren gemäß § 51 EnergieStG in Anspruch genommen oder ist dies zukünftig geplant?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Unternehmen des produzierenden Gewerbes (genaue Definition nach § 2 Nr. 3 " +
      "StromStG anhand der Klassifikation der Wirtschaftszweige des statistischen " +
      "Bundesamts in der Fassung von 2003) können eine Entlastung von der Energiesteuer " +
      "für bestimmte Produktionsprozesse wie bspw. die Erzeugung von Glas, Keramik, " +
      "Ziegel, Beton, Asphalt, etc. sowie für die Metallerzeugung und -bearbeitung " +
      "sowie für chemische Reduktionsverfahren mittels Formular 1115 beim HZA beantragen. " +
      "Eine ausführliche Aufführung aller Herstellungsprozesse findet sich in " +
      "§ 51 Abs. 1 Nr. 1 und 2. Entlastungsfähig ist hierbei insbesondere der Einsatz " +
      "von Kohle sowie Gas und Öl zum Verheizen und zum Antrieb von Gasturbinen und " +
      "Verbrennungsmotoren zur ortsfesten Erzeugung von Strom und gekoppelter Erzeugung " +
      "von Strom und Wärme (§3 StromStG). " +
      "Für Windparks und Freiflächen-PV im Regelfall mit \u201enein\u201c zu beantworten."
  },
  isYearlyReportOfTaxReliefsRequired: {
    name: "isYearlyReportOfTaxReliefsRequired",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Ist eine jährliche Meldung über in Anspruch genommene Strom- bzw. Energiesteuerentlastungen oder -befreiungen erforderlich (EnStransV)?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Insofern eine oder mehrere (§ 54 und § 55) der zuvor aufgeführten " +
      "Energiesteuerentlastungen in Anspruch genommen werden sollen, so müssen die " +
      "entsprechend ausgefüllten Formulare (1115, 1118, 1450) bis zum 31.12. des " +
      "Folgejahrs beim HZA eingereicht werden."
  },
  makesUseOfExemptsOver_500_000PerYear: {
    name: "makesUseOfExemptsOver_500_000PerYear",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Ist davon auszugehen, dass das Unternehmen EEG-Umlagebefreiungen auf Eigenverbrauch in Höhe von 500.000 € oder mehr pro Kalenderjahr in Anspruch nimmt?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Eigenversorger bzw. Eigenerzeuger, bei denen die vollständige oder teilweise EEG-Umlagebefreiung nach den §§ 61 bis 61g EEG 2017 bezogen auf das letzte Kalenderjahr 500.000 € oder mehr beträgt, sind gemäß § 74a Abs. 3 EEG verpflichtet, bis zum 31. Juli des Folgejahres verschiedene Daten (sog. Transparenzdaten) an die Bundesnetzagentur zu melden. Zu den Daten zählen u. a. Angaben zum Umfang der Umlagebefreiung, zur Gebietseinheit und zum Hauptwirtschaftszweig des Eigenversorgers.  Ist der Übertragungsnetzbetreiber für die Erhebung der EEG-Umlage zuständig, verschiebt sich die Frist auf den 31. Oktober des Folgejahres. Für Windparks in der Regel mit \u201eNein\u201c zu beantworten."
  },
  electricityConsumptionOver_50GwhPerYear: {
    name: "electricityConsumptionOver_50GwhPerYear",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "BNetzA Befragung Lastmanagement: Weist dieses Unternehmen einen standortübergreifenden Stromverbrauch > 50 GWh/a auf?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "In einigen Wirtschaftssektoren und Teilsektoren können die CO2-Kosten der Stromkosten besonders stromintensiver Produktionsprozesse teilweise finanziell kompensiert werden, um Produktionsverlagerungen in Läder außerhalb des EU-ETS zu vermeiden. Unternehmen, die Produkte der berechtigten (Teil-)Sektoren (nach NACE-Revision 1.1 (2007) gemäß den EU-Beihilfe-Leitlinien Anhang II) herstellen, können Beihilfen für den Stromverbrauch des abgelaufenen Kalenderjahres der jeweiligen Anlage (Betriebsstätte, ggfs. Abgrenzung nach BImschG) beantragen. Pro Unternehmen ist dabei ein Selbstbehalt in Höhe der CO2-Kosten (EUA-Preis x CO2-Emissionsfaktor) einer GWh je Anlage zu berücksichtigen. Für Windparks in der Regel mit \u201eNein\u201c zu beantworten."
  },
  makesUseOfBesondereAusgleichsregelung: {
    name: "makesUseOfBesondereAusgleichsregelung",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Wird die Besondere Ausgleichsregelung gemäß EEG in Anspruch genommen oder ist dies zukünftig geplant?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Wer Stromsteuer- bzw. Energiesteuerentlastungen in Anspruch nimmt, ist zur Erklärung gegenüber der Zollverwaltung verpflichtet, sofern die Begünstigung in Bezug auf den einzelnen Entlastungstatbestand einen Schwellenwert von 200.000 € je Kalenderjahr übersteigt. Für jeden Entlastungstatbestand des EnergieStG und des StromStG sind in der Erklärung die Angaben gem. § 5 Abs. 2 Nr. 1 bis 7 EnSTransV zu machen. Das heißt: Name, Anschrift und Identifikator des Begünstigten, Art und Menge der im vorangegangen Kalenderjahr verwendeten Energieerzeugnisse bzw. Strom, Höhe der daraus resultierenden Steuerbegünstigung in Euro, Wirtschaftszweig des Begünstigten und ob der Begünstigte zum Zeitpunkt der Inanspruchnahme als Kleinstunternehmen, kleines oder mittleres Unternehmen iSd § 2 Abs. 4 EnSTransV galt. Das Hauptzollamt kann weitere Unterlagen und Angaben verlangen, soweit diese zur Dokumentation der etwaigen Pflichten gegenüber der Kommission oder zur Nachweisführung erforderlich sind. Einschlägige Entlastungstatbestände nach dem Stromsteuer- oder Energiesteuergesetz sind insbesondere: § 9b StromStG (Unternehmen des produzierenden Gewerbes und Unternehmen der Land- und Forstwirtschaft); § 10 StromStG (Spitzenausgleich); § 12c StromStV (Strom aus erneuerbaren Energieträgern); § 12d StromStV (Strom aus hocheffizienten KWK-Anlagen); § 53 a Abs. 1, 4 EnergieStG (gekoppelte Erzeugung von Kraft und Wärme); § 53 a Abs. 6 EnergieStgG (gekoppelte Erzeugung von Kraft und Wärme); § 54 EnergieStG (Unternehmen des produzierenden Gewerbes und Unternehmen der Land- und Forstwirtschaft); § 55 EnergieStG (Spitzenausgleich). Weitere Einschlägige Entlastungstatbestände: § 47a EnergieStG (Eigenverbrauch); § 56 EnergieStG (Öffentlicher Personennahverkehr); § 57 EnergieStG (Betriebe der Land- und Forstwirtschaft), wobei seit 01.07.19 nur noch Abs. 5 Nr. 1 (Gasöle) und Abs. 5 Nr. 2 (Biokraftstoffe); § 9c StromStG (Öffentlicher Personennahverkehr), § 14 a StromStDurchfV (Landstromversorgung) Für Windparks in der Regel mit \u201eNein\u201c zu beantworten."
  },
  makesUseOfBeihilfenStrompreiskompensation: {
    name: "makesUseOfBeihilfenStrompreiskompensation",
    group: PersonWizardTabValue.RegulatoryDuties,
    label:
      "Werden Beihilfen für Strompreiskompensation in Anspruch genommen oder ist dies zukünftig geplant?",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Die Strompreiskompensation soll dazu dienen, die internationale Wettbewerbsfähigkeit insbesondere stromintensiver Unternehmen gegenüber Wettbewerbern aus Weltregionen, welche keiner CO2-Besteuerung unterworfen sind, zu erhalten. Produktionsverlagerungen und somit ein Anstieg der CO2-Emissionen in Ländern außerhalb des EU-Emissionshandelssystems (Carbon Leakage) aufgrund der indirekten CO2-Kosten hoher Strompreise in der EU sollen dadurch verhindert werden. Die Europäische Kommission hat einige Sektoren und Teilsektoren identifiziert, bei denen sie ein derartiges Risiko für indirektes Carbon Leakage sieht, da diese besonders stromintensive Produktionsprozesse beinhalten und starkem internationalem Wettbewerb ausgesetzt sind. Hierunter fällt zum Beispiel die Erzeugung von Roheisen, Stahl und Ferrolegierungen. Grundlage für die Höhe der Kompensation ist u.a. der Preis für Emissionszertifikate in der EU und der CO2-Emissionsfaktor in Höhe von 0,76 Tonnen Kohlendioxid pro Megawattstunde Strom. Für Windparks in der Regel mit \u201eNein\u201c zu beantworten."
  },
  smallMediumEnterprise: {
    name: "smallMediumEnterprise",
    group: PersonWizardTabValue.RegulatoryDuties,
    label: "Kleines oder mittleres Unternehmen gemäß EU-Definition",
    type: "select",
    data: YES_NO_OPTIONS
  },

  // some other fields (not sure yet)
  pensionSalariesPaid: {
    name: "pensionSalariesPaid",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Höhe der rentenversicherungspflichtigen Löhne pro Jahr in Euro",
    type: "number"
  },
  supplyElectricityTaxMonthly: {
    name: "supplyElectricityTaxMonthly",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Stromsteueranmeldung monatlich",
    type: "select",
    data: YES_NO_OPTIONS,
    helpText:
      "Die Stromsteueranmeldung ist entweder monatlich oder jährlich fällig"
  },
  taxFormsApplicantDateOfBirthOrFoundation: {
    name: "taxFormsApplicantDateOfBirthOrFoundation",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Geburtsdatum bzw. Gründungsdatum",
    type: "date",
    rightSection: <IconCalendarWeek size="1.5rem" />,
    helpText:
      "Bitte beachten Sie, dass je nach Rechtsform das Gründungsdatum im späteren Formular gleichbedeutend mit Geburtsdatum sein kann."
  },
  besondereAusgleichsregelungEeg: {
    name: "besondereAusgleichsregelungEeg",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Besondere Ausgleichsregelung EEG: Tarif über 1 Mio. kWh",
    type: "number"
  },
  besondereAusgleichsregelungKwk: {
    name: "besondereAusgleichsregelungKwk",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Besondere Ausgleichsregelung KWK-Aufschlag: Tarif über 1 Mio. kWh",
    type: "number"
  },
  besondereAusgleichsregelungOffshore: {
    name: "besondereAusgleichsregelungOffshore",
    group: PersonWizardTabValue.TaxFormulae,
    label:
      "Besondere Ausgleichsregelung Offshore-Netzumlage: Tarif über 1 Mio. kWh",
    type: "number"
  },
  isStrompbgCalculatorActive: {
    name: "isStrompbgCalculatorActive",
    group: PersonWizardTabValue.TaxFormulae,
    label:
      "Soll das Add-on \u201eErlösabschöpfung nach StromPBG\u201c für dieses Unternehmen aktiviert werden?",
    type: "boolean"
  },
  versorgerstatusPeriods: {
    name: "versorgerstatusPeriods",
    group: PersonWizardTabValue.TaxFormulae,
    type: "custom",
    label:
      "Verfügt das Unternehmen über eine Erlaubnis als Versorger oder Eigenerzeuger?*",
    helpText:
      "Laut § 4 StromStG bedarf es einer Erlaubnis, um Strom an Dritte zu liefern oder als Eigenerzeuger Strom zum Selbstverbrauch zu entnehmen, der nicht nach §9 Abs. 1 Nr. 3a befreit ist. Dieser Antrag ist beim zuständigen Hauptzollamt zu stellen. \n\n Hierfür steht das Formular 1410 (Antrag oder Anzeige auf Erteilung oder Änderung einer Erlaubnis als Versorger, eingeschränkter Versorger, Eigenerzeuger oder erlaubnispflichtiger Letztverbraucher) in der Aufgabe „Einholen Erlaubnis als Eigenerzeuger/kleiner Versorger“ zur Verfügung."
  },
  customsOffice: {
    name: "customsOffice",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Zuständiges Hauptzollamt",
    type: "text",
    helpText:
      "Über die [Dienststellensuche der GZD](https://www.zoll.de/DE/Kontakt/Dienststellensuche/Startseite/dienststellensuche_node.html) können Sie das für Ihr Unternehmen zuständige HZA ermitteln. Hintergrund: Die Abwicklung strom- und energiesteuerlicher Sachverhalte (Steuerentlastungen, Anträge etc.) erfolgt gegenüber dem zuständigen Hauptzollamt (HZA). Für jedes Unternehmen ist (nur) ein Hauptzollamt zuständig. Gemäß Generalzolldirektion (GZD) gilt das HZA als örtlich zuständig, von dessen Bezirk aus ein Unternehmen betrieben wird. Ein Unternehmen wird dort betrieben, wo der Unternehmer seine Tätigkeit anbietet, Aufträge entgegennimmt, ihre Ausführung vorbereitet und wo Zahlungen an ihn geleistet werden; das wird i. d. R. der Ort der Willensbildung, mithin der Geschäftsleitung sein. Für Unternehmen, die von einem Ort außerhalb des Steuergebiets betrieben werden, ist das Hauptzollamt örtlich zuständig, in dessen Bezirk sie erstmalig steuerlich in Erscheinung treten."
  },

  // Tax form fields
  // Allgemein
  taxFormsSubmissionInOwnName: {
    name: "taxFormsSubmissionInOwnName",
    group: PersonWizardTabValue.TaxFormulae,
    label:
      "Sollen Anträge für das Hauptzollamt in eigenem Namen eingereicht werden?",
    type: "boolean"
  },
  taxFormsCompanyRepresentedBy: {
    name: "taxFormsCompanyRepresentedBy",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Name des vertretenden Unternehmens",
    type: "select",
    data: [] // Will be populated dynamically with persons
  },
  taxFormsRepresentationType: {
    name: "taxFormsRepresentationType",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Art des Vertretungsverhältnisses",
    type: "select",
    data: REPRESENTATION_TYPE_OPTIONS
  },

  // Antragsteller Steuerformulare
  taxFormsApplicantName: {
    name: "taxFormsApplicantName",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Name bzw. Firmenbezeichnung",
    type: "text"
  },
  taxFormsApplicantStreet: {
    name: "taxFormsApplicantStreet",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Straße",
    type: "text"
  },
  taxFormsApplicantHouseNumber: {
    name: "taxFormsApplicantHouseNumber",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Nummer",
    type: "text"
  },
  taxFormsApplicantAddressSupplement: {
    name: "taxFormsApplicantAddressSupplement",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Adresszusatz",
    type: "text"
  },
  taxFormsApplicantZip: {
    name: "taxFormsApplicantZip",
    group: PersonWizardTabValue.TaxFormulae,
    label: "PLZ",
    type: "text"
  },
  taxFormsApplicantCity: {
    name: "taxFormsApplicantCity",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Ort",
    type: "text"
  },
  taxFormsApplicantCityDistrict: {
    name: "taxFormsApplicantCityDistrict",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Ortsteil",
    type: "text"
  },
  taxFormsApplicantCountry: {
    name: "taxFormsApplicantCountry",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Land",
    type: "select",
    data: TAX_FORMS_APPLICANT_COUNTRY_OPTIONS
  },
  taxFormsApplicantResidenceOrOffice: {
    name: "taxFormsApplicantResidenceOrOffice",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Ggf. Geschäfts- oder Wohnsitz",
    type: "text"
  },
  taxFormsApplicantOtherCity: {
    name: "taxFormsApplicantOtherCity",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Ggf. abweichender Ort der Geschäftsleitung",
    type: "text"
  },

  // EU-Beihilferecht
  unternehmenInSchwierigkeitenYears: {
    name: "unternehmenInSchwierigkeitenYears",
    group: PersonWizardTabValue.TaxFormulae,
    label:
      "Dieses Unternehmen wird für folgende Jahre als Unternehmen in Schwierigkeiten eingestuft",
    type: "multiselect",
    data: Array.from(
      { length: new Date().getFullYear() - 2020 + 1 },
      (_, i) => ({ value: String(2020 + i), label: String(2020 + i) })
    )
  },

  // Zusatzinformationen für die HZA-Kommunikation
  taxFormsApplicantNumber: {
    name: "taxFormsApplicantNumber",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Beteiligten-Nr. (VVSt)",
    maxLength: 12,
    type: "text"
  },
  companyNumber: {
    name: "companyNumber",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Unternehmens-Nr. beim Hauptzollamt",
    maxLength: 5,
    type: "text",
    helpText:
      "Beim ersten Kontakt Ihres Unternehmens mit dem Hauptzollamt erhalten Sie eine fünfstellige Unternehmens-Nummer. Diese dient bei jedem weiteren Kontakt zur eindeutigen Identifikation Ihres Unternehmens. Falls vorhanden, tragen Sie bitte diese Nummer hier ein."
  },
  taxFormsApplicantTransactionNumberElectricityTax: {
    name: "taxFormsApplicantTransactionNumberElectricityTax",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Kassenzeichen für die Stromsteuer",
    type: "text",
    helpText:
      "Das Kassenzeichen setzt sich zusammen aus 'M05' gefolgt von weiteren 9 Ziffern. Bspw. 'M05123456789'"
  },

  // Ansprechpartner Steuerformulare
  taxFormsContactName: {
    name: "taxFormsContactName",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Name",
    type: "text"
  },
  taxFormsContactPhone: {
    name: "taxFormsContactPhone",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Telefon",
    type: "text"
  },
  taxFormsContactFax: {
    name: "taxFormsContactFax",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Fax",
    type: "text"
  },
  taxFormsContactEmail: {
    name: "taxFormsContactEmail",
    group: PersonWizardTabValue.TaxFormulae,
    label: "E-Mail",
    type: "email",
    maxLength: 254
  },
  taxFormsContactWebsite: {
    name: "taxFormsContactWebsite",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Internet-Adresse",
    type: "text"
  },

  // Bankverbindung Steuerformulare
  taxFormsBankAccountHolder: {
    name: "taxFormsBankAccountHolder",
    group: PersonWizardTabValue.TaxFormulae,
    label: "Kontoinhaber",
    type: "text"
  },
  taxFormsBankAccountIban: {
    name: "taxFormsBankAccountIban",
    group: PersonWizardTabValue.TaxFormulae,
    label: "IBAN",
    type: "text",
    maxLength: 34
  },
  taxFormsBankAccountBic: {
    name: "taxFormsBankAccountBic",
    group: PersonWizardTabValue.TaxFormulae,
    label: "BIC",
    type: "text",
    maxLength: 11
  },

  // Invoice Supplier fields - Stromlieferung und Abrechnung
  invoiceSupplierPhone: {
    name: "invoiceSupplierPhone",
    group: PersonWizardTabValue.Deliveries,
    label: "Telefon",
    type: "text",
    helpText:
      "Nach §40 Abs. 2 Nr. 1 EnWG ist eine Telefonnummer zur Kontaktaufnahme für den Belieferten bei Rückfragen eine zwingende Voraussetzung für eine ordnungsgemäße Stromrechnung."
  },
  invoiceSupplierFax: {
    name: "invoiceSupplierFax",
    group: PersonWizardTabValue.Deliveries,
    label: "Fax",
    type: "text",
    helpText:
      "Insofern Sie eine Fax-Nummer auf der Stromrechnung ausweisen möchten, tragen Sie diese hier ein. Eine Fax-Nummer ist keine gesetzliche Anforderung und muss auf der Stromrechnung nicht ausgewiesen werden."
  },
  invoiceSupplierEmail: {
    name: "invoiceSupplierEmail",
    group: PersonWizardTabValue.Deliveries,
    label: "E-Mail Adresse für Kontaktinformation und Rechnungsversand",
    type: "email",
    maxLength: 254,
    helpText:
      "Laut §40 Abs. 2 Nr. 1 EnWG ist eine E-Mail Adresse für die elektronische Kontaktaufnahme eine zwingende Voraussetzung für eine ordnungsgemäße Stromrechnung. Bei manuellem oder automatischem Versand der Rechnung per E-Mail aus opti.node wird die Rechnung in CC an diese E-Mail-Adresse verschickt."
  },
  invoiceSupplierUrl: {
    name: "invoiceSupplierUrl",
    group: PersonWizardTabValue.Deliveries,
    label: "Website",
    type: "text",
    helpText:
      "Falls Sie mit der Stromrechnung auf Ihre Website hinweisen möchten, dann können Sie diese hier hinterlegen."
  },
  invoiceSupplierCeo: {
    name: "invoiceSupplierCeo",
    group: PersonWizardTabValue.Deliveries,
    label: "Geschäftsführer",
    type: "text",
    helpText:
      "Gemäß §125a HGB, §35a GmbHG und §80 AktG sind Lieferanten verpflichtet, alle Geschäftsführer mit Vor- und Familiennamen auf Geschäftsbriefen auszuweisen, hierunter fällt auch die Rechnungskorrespondenz. Wird die Rechnung von einer natürlichen Person ausgestellt, so ist diese Anforderung gleichfalls durch die Benennung des Namens des Energielieferanten erfüllt."
  },
  invoiceSupplierIsGbr: {
    name: "invoiceSupplierIsGbr",
    group: PersonWizardTabValue.Deliveries,
    label: "Handelt es sich um eine GbR (bzw. GdbR, BGB-Gesellschaft)?",
    type: "select",
    data: YES_NO_OPTIONS
  },
  invoiceSupplierCourt: {
    name: "invoiceSupplierCourt",
    group: PersonWizardTabValue.Deliveries,
    label: "Amtsgericht",
    type: "text",
    helpText:
      "Gemäß § 40 Abs. 2 Nr. 1 EnWG sowie § 125a Abs. 1 HGB ist die Angabe des zuständigen Registergerichts auf einer Rechnung notwendig."
  },
  invoiceSupplierRegisterNumber: {
    name: "invoiceSupplierRegisterNumber",
    group: PersonWizardTabValue.Deliveries,
    label: "Registernummer",
    type: "text",
    helpText:
      "Gemäß § 125a Abs. 1 HGB ist das zuständige Amtsgericht/Registergericht zusätzlich um die von diesem Amtsgericht zugeordnete Registernummer der Gesellschaft zu ergänzen. Die Registernummer ist im Handelsregister hinterlegt."
  },
  invoiceSupplierLocation: {
    name: "invoiceSupplierLocation",
    group: PersonWizardTabValue.Deliveries,
    label: "Sitz der Gesellschaft",
    type: "text",
    helpText:
      "Gemäß § 125a Abs. 1 HGB ist auf der Unternehmenskorrespondenz, worunter auch Rechnungen einzuordnen sind, der Sitz der Gesellschaft anzugeben."
  },
  invoiceSupplierLogo: {
    name: "invoiceSupplierLogo",
    group: PersonWizardTabValue.Deliveries,
    label: "Unternehmenslogo",
    type: "uploadable-image",
    removeButtonText: "Logo löschen",
    resourceImageFieldName: "invoice_supplier_logo",
    deleteUrl: urls.api.deletePersonImage,
    uploadUrl: urls.api.uploadPersonImage,
    helpText:
      "Mit Ihrem Unternehmenslogo können Sie die erzeugten Stromrechnungen individualisieren. Das hinterlegte Logo wird auf den Rechnungen erscheinen. Hierfür können Sie Dateien im Format jpeg und png verwenden. Die Dateigröße sollte 2 MB nicht überschreiten."
  },
  invoiceSupplierBank: {
    name: "invoiceSupplierBank",
    group: PersonWizardTabValue.Deliveries,
    label: "Bank",
    type: "text",
    helpText:
      "Hinterlegen Sie den Namen Ihrer Bank, insofern Sie diese auf der Rechnung ausweisen möchten. Bspw. um Überweisungen für den ausgewiesenen Rechnungsbetrag zu ermöglichen. Die Angabe einer Bankverbindung ist keine gesetzliche Anforderung an eine Rechnung, wird häufig jedoch mit ausgewiesen."
  },
  invoiceSupplierIban: {
    name: "invoiceSupplierIban",
    group: PersonWizardTabValue.Deliveries,
    label: "IBAN",
    type: "text",
    helpText:
      "Hinterlegen Sie Ihre IBAN, insofern Sie diese auf der Rechnung ausweisen möchten. Bspw. um Überweisungen für den ausgewiesenen Rechnungsbetrag zu ermöglichen. Die Angabe einer Bankverbindung ist keine gesetzliche Anforderung an eine Rechnung, wird häufig jedoch mit ausgewiesen."
  },
  invoiceSupplierBic: {
    name: "invoiceSupplierBic",
    group: PersonWizardTabValue.Deliveries,
    label: "BIC",
    type: "text",
    helpText:
      "Der BIC ist im Normalfall nur für internationale Überweisungen notwendig."
  },
  invoiceSupplierCreditorId: {
    name: "invoiceSupplierCreditorId",
    group: PersonWizardTabValue.Deliveries,
    label: "Gläubiger ID",
    type: "text",
    helpText:
      "Die Gläubiger ID ist notwendig, wenn Sie den Rechnungsbetrag über ein Lastschriftverfahren \nvom Belieferten direkt einziehen möchten. Die Gläubiger ID ermöglicht die eindeutige \nIdentifizierung eines Gläubigers, also des Zahlungsempfängers. Die Gläubiger ID muss bei \nder Deutschen Bundesbank beantragt werden. Die Gläubiger ID kann Online beantragt werden \n([www.glaeubiger-id.bundesbank.de](www.glaeubiger-id.bundesbank.de)) und erfordert \nzusätzlich eine Mandatsreferenz (→ diese können Sie auf dem jeweiligen Vertrag hinterlegen), \num das Mandat einer Lastschrift eindeutig zu identifizieren.\n"
  },
  invoiceSupplierTaxNumber: {
    name: "invoiceSupplierTaxNumber",
    group: PersonWizardTabValue.Deliveries,
    label: "Steuernummer",
    type: "text",
    helpText:
      "Insofern Sie bereits eine USt-ID hinterlegt haben, ist die Steuernummer nicht zwingend zusätzlich erforderlich. Gemäß § 14 Abs. 4 Nr. 2 UstG ist in jedem Fall mindestens entweder die USt-ID oder die Steuernummer auf einer Rechnung auszuweisen. Die Steuernummer wird vom Finanzamt erteilt."
  },
  invoiceSupplierSalesTaxId: {
    name: "invoiceSupplierSalesTaxId",
    group: PersonWizardTabValue.Deliveries,
    label: "USt. ID",
    type: "text",
    helpText:
      "Insofern Sie bereits eine Steuernummer hinterlegt haben, ist die Umsatzsteuer-Identifikationsnummer (USt-ID) nicht zwingend zusätzlich erforderlich. Gemäß § 14 Abs. 4 Nr. 2 UstG ist in jedem Fall mindestens entweder die USt-ID oder die Steuernummer auf einer Rechnung auszuweisen. Die USt-ID wird vom Bundeszentralamt für Steuern erteilt."
  },

  // Invoice Supplied fields - Stromlieferung und Abrechnung (delivered fields)
  invoiceSuppliedAddressIsGeneralAddress: {
    name: "invoiceSuppliedAddressIsGeneralAddress",
    group: PersonWizardTabValue.Deliveries,
    label:
      "Gleicht die Rechnungsanschrift der Anschrift unter \u201eAllgemeine Daten\u201c?",
    type: "select",
    data: YES_NO_OPTIONS
  },
  invoiceSuppliedAddress: {
    name: "invoiceSuppliedAddress",
    group: PersonWizardTabValue.Deliveries,
    label: "Rechnungsanschrift Straße und Hausnummer",
    type: "text"
  },
  invoiceSuppliedAddressExtra: {
    name: "invoiceSuppliedAddressExtra",
    group: PersonWizardTabValue.Deliveries,
    label: "Rechnungsanschrift Zusatz",
    type: "text"
  },
  invoiceSuppliedZip: {
    name: "invoiceSuppliedZip",
    group: PersonWizardTabValue.Deliveries,
    label: "Rechnungsanschrift PLZ",
    type: "text",
    maxLength: 10
  },
  invoiceSuppliedCity: {
    name: "invoiceSuppliedCity",
    group: PersonWizardTabValue.Deliveries,
    label: "Rechnungsanschrift Ort",
    type: "text"
  },
  invoiceSuppliedDebitNumber: {
    name: "invoiceSuppliedDebitNumber",
    group: PersonWizardTabValue.Deliveries,
    label: "Debitorennummer",
    type: "text"
  },
  invoiceSuppliedAccountingArea: {
    name: "invoiceSuppliedAccountingArea",
    group: PersonWizardTabValue.Deliveries,
    label: "Buchungskreis Rechnungsaussteller",
    type: "text"
  },
  invoiceSuppliedTargetAccountingArea: {
    name: "invoiceSuppliedTargetAccountingArea",
    group: PersonWizardTabValue.Deliveries,
    label: "Zielbuchungskreis",
    type: "text"
  },
  invoiceSuppliedBusinessArea: {
    name: "invoiceSuppliedBusinessArea",
    group: PersonWizardTabValue.Deliveries,
    label: "Geschäftsbereich",
    type: "text"
  },
  invoiceSuppliedCostCenter: {
    name: "invoiceSuppliedCostCenter",
    group: PersonWizardTabValue.Deliveries,
    label: "Kostenstelle",
    type: "text"
  },
  invoiceSuppliedPspElement: {
    name: "invoiceSuppliedPspElement",
    group: PersonWizardTabValue.Deliveries,
    label: "PSP-Element",
    type: "text"
  },
  invoiceSuppliedProfitCenter: {
    name: "invoiceSuppliedProfitCenter",
    group: PersonWizardTabValue.Deliveries,
    label: "Profitcenter Nr.",
    type: "text"
  },
  invoiceSuppliedGlobalId: {
    name: "invoiceSuppliedGlobalId",
    group: PersonWizardTabValue.Deliveries,
    label: "GLN (Global Location Number)",
    type: "text"
  },
  invoiceSuppliedInformationCategory: {
    name: "invoiceSuppliedInformationCategory",
    group: PersonWizardTabValue.Deliveries,
    label: "Kategorie",
    type: "text",
    helpText:
      "Insofern Sie für diesen Belieferten eine individuelle Information auf der Rechnung ausweisen möchten, können Sie dies hier tun. Der (Kurz)Text wird auf den Stammdaten zu Beginn der Rechnung erscheinen. Sie haben mit diesem Feld die Möglichkeit, eine Kategorie anzugeben (bestehende Kategorien sind bspw. Produktbezeichnung, Rechnungsdatum/nummer, Leistungszeitraum) sowie mit dem nachfolgenden Feld dazugehörige, individuelle Informationen zu hinterlegen. Ein Beispiel wäre _Kundengruppe_ und _Großverbraucher_ oder _Lieferanten-ID_ und _17308.11-18_ aber auch _Kundengruppe_ und _grün_ oder _Geburtstag_ und _Herzlichen Glückwunsch_. So lange diese beiden Felder - oder eines der Felder - befüllt sind, wird diese Information mit auf die Rechnung geschrieben."
  },
  invoiceSuppliedInformation: {
    name: "invoiceSuppliedInformation",
    group: PersonWizardTabValue.Deliveries,
    label: "Information/Kurztext",
    type: "text",
    helpText:
      "Ergänzen Sie für die zuvor definierte Kategorie um einen individuellen Text."
  },
  invoiceSuppliedCustomerNumber: {
    name: "invoiceSuppliedCustomerNumber",
    group: PersonWizardTabValue.Deliveries,
    label: "Kundennummer",
    type: "text",
    helpText:
      "Insofern Sie eine Kundennummer verwenden, um den Kunden eindeutig zu identifizieren, können Sie diese auf der Rechnung ausweisen. Dies ist keine Pflichtangabe auf einer Stromrechnung und dient lediglich der Vereinfachung der Kommunikation mit dem Kunden. Insbesondere dann, wenn Sie sehr viele Kunden haben können Sie hierüber einen Kunden sehr schnell und einfach identifizieren."
  },
  invoiceSuppliedEmail: {
    name: "invoiceSuppliedEmail",
    group: PersonWizardTabValue.Deliveries,
    label: "E-Mail Adresse für Rechnungsempfang",
    type: "email"
  },
  additionalInvoiceSuppliedEmails: {
    name: "additionalInvoiceSuppliedEmails",
    group: PersonWizardTabValue.Deliveries,
    label: "Zusätzliche E-Mail Adressen für Rechnungsempfang",
    type: "multiple-emails"
  },
  invoiceSuppliedBusinessReference: {
    name: "invoiceSuppliedBusinessReference",
    group: PersonWizardTabValue.Deliveries,
    label: "Geschäftszeichen",
    type: "text",
    helpText:
      "Ein Geschäftszeichen kann genutzt werden, um die Rechnungen einem Geschäftsvorgang zuzuordnen. Insofern Sie auf der Rechnung ein Geschäftszeichen ausweisen möchten, können Sie dieses hier hinterlegen. Für eine Stromrechnung ist ein Geschäftszeichen nicht verpflichtend."
  },
  invoiceSuppliedBank: {
    name: "invoiceSuppliedBank",
    group: PersonWizardTabValue.Deliveries,
    label: "Bank",
    type: "text",
    helpText:
      "Insofern Sie das Lastschriftverfahren gewählt haben, ist das Ausweisen der Bankverbindung des Kunden auf der Stromrechnung erforderlich."
  },
  invoiceSuppliedIban: {
    name: "invoiceSuppliedIban",
    group: PersonWizardTabValue.Deliveries,
    label: "IBAN",
    type: "text",
    helpText:
      "Insofern Sie das Lastschriftverfahren gewählt haben, ist das Ausweisen der Bankverbindung des Kunden auf der Stromrechnung erforderlich."
  },
  invoiceSuppliedBic: {
    name: "invoiceSuppliedBic",
    group: PersonWizardTabValue.Deliveries,
    label: "BIC",
    type: "text",
    helpText:
      "Der BIC ist im Normalfall nur für internationale Überweisungen notwendig."
  },

  // Invoice Recipient field
  invoiceRecipient: {
    name: "invoiceRecipient",
    group: PersonWizardTabValue.Deliveries,
    label: "Rechnungsempfänger",
    type: "text",
    helpText:
      "Ergänzen Sie diejenige Person oder Gesellschaft, an welche die Stromrechnung adressiert werden soll. Eine Angabe ist nur notwendig, wenn die Rechnungsadresse von der Anschrift (Reiter Allgemeine Daten) abweicht."
  }
} as const satisfies {
  [key in keyof Person]?: FormInputData<Person, key>;
};

export const UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_INFO_TEXT =
  "Prüfen Sie bitte zur Beantwortung dieser Frage intern Ihren Status ab. " +
  "Relevante Informationen sowie weiterführende Links finden Sie hier: " +
  "[Unternehmen in Schwierigkeiten](https://support.node.energy/knowledge/unternehmen-in-schwierigkeiten)";

export const UNTERNEHMEN_IN_SCHWIERIGKEITEN_YEARS_CHANGED_TOAST_MESSAGE =
  "Sie haben den Status bei “Unternehmen in Schwierigkeiten” geändert. Daher " +
  "gelten für Ihr Unternehmen nun neue Entlastungsbestände, die sich auf Ihre " +
  "Meldeunterlagen auswirken. Wir bereiten Ihre neuen Meldeunterlagen vor und " +
  "geben Ihnen Bescheid, sobald diese fertig sind.";

export const TAX_FORMS_APPLICANT_NUMBER_INFO_TEXT =
  "Die aufgeführten Informationen können unter Umständen die Kommunikation mit " +
  "dem HZA erleichtern, sind aber nicht in jedem Fall bekannt. Insofern Sie " +
  "die Informationen ganz oder teilweise haben, tragen Sie diese hier ein.";

export const TAX_FORMS_CONTACT_NAME_INFO_TEXT =
  "Das Hauptzollamt benötigt einen Ansprechpartner für Rückfragen. " +
  "Das können Sie selbst oder Ihr Steuerberater sein. Bitte geben Sie hier die Kontaktdaten ein.";
